import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { apiFetch } from "@/lib/api";

import type { AeForecastRow } from "../api";
import { useUpsertWeeklyCall } from "../api";
import { hubspotDealUrl } from "../constants";
import { fmtCents, fmtCentsWhole, fmtMonthName, fmtPct } from "../format";
import type { CroForecastSearch } from "../search";

/**
 * Drawer-based per-AE forecast editor on the Forecast Meeting tab.
 *
 * Replaces the prior inline expand-row UX. Follows the cinch_ops standard
 * drawer pattern (quick → expanded continuum, URL-driven via
 * ``?rep=<repId>&expanded=1``) so the page UX matches the rest of the app.
 *
 * In quick mode (drawer at 480px on the right): per-month Call inputs +
 * Forecast Submission summary + override slider + per-AE notes.
 *
 * In expanded mode: the same controls plus the AE's open-deals list, the
 * link to the dedicated AE Detail page, and room for the Phase-2 weekly
 * call trajectory chart.
 */
export function AeForecastDrawer({
  rep,
  perAeNotesBlob,
  isoYear,
  isoWeek,
  targetYear,
  currentFiscalMonth,
}: {
  rep: AeForecastRow | null;
  perAeNotesBlob: Record<string, string>;
  isoYear: number;
  isoWeek: number;
  targetYear: number;
  /** Fiscal month of the viewed iso-week. Months in the quarter that
   *  fall BEFORE this are rendered read-only (showing closed amount)
   *  — Stan can't change a forecast for a month that already ended. */
  currentFiscalMonth: number;
}) {
  const navigate = useNavigate();
  const search = useRouterState({ select: (s) => s.location.search }) as CroForecastSearch;
  const open = rep != null;
  const expanded = !!search.expanded;

  function setDrawer(next: { rep?: string; expanded?: boolean } | null) {
    void navigate({
      to: "/cro-forecast/forecast-meeting",
      search: (prev: Record<string, unknown>) => {
        const { rep: _r, expanded: _e, ...rest } = (prev as Record<string, unknown>) ?? {};
        if (!next || !next.rep) return rest;
        return { ...rest, rep: next.rep, ...(next.expanded ? { expanded: true } : {}) };
      },
      replace: true,
    });
  }

  return (
    <EntityDrawer
      open={open}
      onOpenChange={(v) => {
        if (!v) setDrawer(null);
      }}
      expanded={expanded}
      onExpandedChange={(v) =>
        rep && setDrawer({ rep: rep.rep_id, expanded: v })
      }
      title={rep ? rep.display_name : ""}
      subtitle={
        rep ? (
          <span className="flex items-center gap-2">
            <span className="uppercase tracking-wider">{rep.tier}</span>
            <span>·</span>
            <span>
              Q-Call {fmtCents(rep.quarter_call_cents)} · Pipeline{" "}
              {fmtCents(rep.quarter_pipeline_cents)}
              {rep.quarter_coverage != null && (
                <> · {rep.quarter_coverage.toFixed(2)}× coverage</>
              )}
            </span>
          </span>
        ) : null
      }
    >
      {rep && (
        <DrawerBody
          rep={rep}
          perAeNotesBlob={perAeNotesBlob}
          isoYear={isoYear}
          isoWeek={isoWeek}
          targetYear={targetYear}
          currentFiscalMonth={currentFiscalMonth}
          expanded={expanded}
        />
      )}
    </EntityDrawer>
  );
}

function DrawerBody({
  rep,
  perAeNotesBlob,
  isoYear,
  isoWeek,
  targetYear,
  currentFiscalMonth,
  expanded,
}: {
  rep: AeForecastRow;
  perAeNotesBlob: Record<string, string>;
  isoYear: number;
  isoWeek: number;
  targetYear: number;
  currentFiscalMonth: number;
  expanded: boolean;
}) {
  const callMutation = useUpsertWeeklyCall();
  const noteMutation = usePerAeNote(isoYear, isoWeek, perAeNotesBlob);
  const overrideMutation = useUpsertOverride();
  const qc = useQueryClient();

  // Editable per-month CRO calls.
  const initialCalls = useMemo(
    () => Object.fromEntries(rep.months.map((m) => [m.month, m.call_cents])),
    [rep.months],
  );
  const [draftCall, setDraftCall] = useState<Record<number, number>>(initialCalls);

  // Forecast Submission is per-month. We default to the *current fiscal
  // month*'s HubSpot-sourced submission, pre-filling the input with what
  // the AE has effectively submitted in HubSpot. Stan edits + saves; the
  // saved value lands in weekly_ae_calls. The fiscal month is the
  // month containing the viewed iso-week — NOT today's calendar month
  // — so when Stan navigates back to Wk 17 (April), the drawer treats
  // April as current.
  const submissionMonthRow = useMemo(
    () =>
      rep.months.find((m) => m.month === currentFiscalMonth) ??
      rep.months[rep.months.length - 1] ??
      null,
    [rep.months, currentFiscalMonth],
  );
  const hubspotSubmissionCents = submissionMonthRow?.hubspot_submission_cents ?? 0;
  // Per-month draft of submissions. Pre-fill order:
  // 1. The most recent locked weekly_ae_calls row (``submitted_cents``).
  // 2. Otherwise fall back to HubSpot's projected value (``hubspot_submission_cents``).
  // Locked rows are the durable record — they survive HubSpot data shifts.
  const initialSubmissions = useMemo(
    () =>
      Object.fromEntries(
        rep.months.map((m) => [
          m.month,
          m.submitted_cents ?? m.hubspot_submission_cents ?? 0,
        ]),
      ),
    [rep.months],
  );
  const initialReasoning = useMemo(
    () =>
      Object.fromEntries(
        rep.months.map((m) => [m.month, m.submitted_reasoning ?? ""]),
      ),
    [rep.months],
  );
  const [draftSubmission, setDraftSubmission] =
    useState<Record<number, number>>(initialSubmissions);
  const [draftReasoning, setDraftReasoning] =
    useState<Record<number, string>>(initialReasoning);

  useEffect(() => {
    // Reset drafts whenever the rep changes.
    setDraftCall(initialCalls);
    setDraftSubmission(initialSubmissions);
    setDraftReasoning(initialReasoning);
  }, [rep.rep_id, initialCalls, initialSubmissions, initialReasoning]);
  void hubspotSubmissionCents; // touchpoint for future "current week" derivation

  const [note, setNote] = useState<string>(rep.notes ?? "");
  useEffect(() => {
    setNote(rep.notes ?? "");
  }, [rep.notes]);

  const [overridePct, setOverridePct] = useState<number>(100);

  function saveCall(month: number) {
    callMutation.mutate(
      {
        iso_year: isoYear,
        iso_week: isoWeek,
        rep_id: rep.rep_id,
        target_period_type: "month",
        target_year: targetYear,
        target_period: month,
        call_cents: draftCall[month] ?? 0,
      },
      {
        onSuccess: () =>
          toast.success(`${fmtMonthName(month)} call saved for ${rep.display_name}`),
        onError: (e) => toast.error(`Save failed: ${(e as Error).message}`),
      },
    );
  }

  function saveSubmissionFor(month: number) {
    // Each save is a new row in weekly_ae_calls keyed by (iso_year, iso_week,
    // rep_id, target_period_type, target_year, target_period). Re-saving in
    // the same week UPDATES; in a new week it creates a new history entry.
    // We tag ``source`` based on whether the user edited the HubSpot-sourced
    // value at all: untouched → "hubspot_forecast_module", changed → "manual_entry".
    const monthData = rep.months.find((m) => m.month === month);
    const fromHubspot = monthData?.hubspot_submission_cents ?? 0;
    const value = draftSubmission[month] ?? 0;
    const reasoning = draftReasoning[month] || null;
    const source =
      value === fromHubspot && fromHubspot > 0
        ? "hubspot_forecast_module"
        : "manual_entry";
    apiFetch(`/cro_forecast/ae-call`, {
      method: "PUT",
      body: {
        iso_year: isoYear,
        iso_week: isoWeek,
        rep_id: rep.rep_id,
        target_period_type: "month",
        target_year: targetYear,
        target_period: month,
        call_cents: value,
        source,
        reasoning,
      },
    })
      .then(() =>
        toast.success(
          `${fmtMonthName(month)} submission saved for ${rep.display_name} (Wk ${isoWeek})`,
        ),
      )
      .catch((e) => toast.error(`Save failed: ${(e as Error).message}`));
  }

  function resetSubmissionFor(month: number) {
    const m = rep.months.find((x) => x.month === month);
    // Reset back to the locked-in submission if one exists, else HubSpot.
    const baseline = m?.submitted_cents ?? m?.hubspot_submission_cents ?? 0;
    setDraftSubmission({ ...draftSubmission, [month]: baseline });
    setDraftReasoning({ ...draftReasoning, [month]: m?.submitted_reasoning ?? "" });
  }

  function saveNote() {
    noteMutation.mutate(
      { rep_id: rep.rep_id, body: note },
      {
        onSuccess: () => toast.success(`Notes saved`),
        onError: (e) => toast.error(`Save failed: ${(e as Error).message}`),
      },
    );
  }

  function saveOverride() {
    overrideMutation.mutate(
      {
        iso_year: isoYear,
        iso_week: isoWeek,
        rep_id: rep.rep_id,
        override_pct: overridePct,
      },
      {
        onSuccess: () => toast.success(`Override saved`),
        onError: (e) => toast.error(`Save failed: ${(e as Error).message}`),
      },
    );
  }

  // Apply Stan's weekly % across every still-open month: each month's CRO
  // call becomes pct × that month's HubSpot Forecast Submission (e.g. 80% of a
  // $100K HubSpot month → $80K). Past (closed) months are left untouched —
  // their calls are settled. Stan can fine-tune any month afterward, then
  // Save all. The % itself is also recorded for the week.
  function applyOverride() {
    const factor = overridePct / 100;
    setDraftCall((prev) => {
      const next = { ...prev };
      for (const m of rep.months) {
        if (m.month < currentFiscalMonth) continue;
        next[m.month] = Math.round((m.hubspot_submission_cents ?? 0) * factor);
      }
      return next;
    });
    saveOverride();
  }

  // Persist every still-open month whose call differs from what's saved — the
  // one click that pushes the applied % through to the Summary rollup. The
  // per-month Save buttons still work for one-off tweaks.
  async function saveAllCalls() {
    const dirtyMonths = rep.months.filter(
      (m) =>
        m.month >= currentFiscalMonth &&
        (draftCall[m.month] ?? 0) !== m.call_cents,
    );
    if (dirtyMonths.length === 0) return;
    try {
      await Promise.all(
        dirtyMonths.map((m) =>
          callMutation.mutateAsync({
            iso_year: isoYear,
            iso_week: isoWeek,
            rep_id: rep.rep_id,
            target_period_type: "month",
            target_year: targetYear,
            target_period: m.month,
            call_cents: draftCall[m.month] ?? 0,
          }),
        ),
      );
      toast.success(
        `Saved ${dirtyMonths.length} month${
          dirtyMonths.length === 1 ? "" : "s"
        } for ${rep.display_name}`,
      );
      void qc.invalidateQueries({
        queryKey: ["cro_forecast", "forecast-meeting"],
      });
    } catch (e) {
      toast.error(`Save failed: ${(e as Error).message}`);
    }
  }

  const anyCallDirty = rep.months.some(
    (m) =>
      m.month >= currentFiscalMonth &&
      (draftCall[m.month] ?? 0) !== m.call_cents,
  );

  return (
    <div className={`space-y-5 p-4 ${expanded ? "md:p-6" : ""}`}>
      {/* Forecast Submission — per month, with reasoning + locked weekly
          history. The historical, point-in-time record lives in
          ``weekly_ae_calls`` (Cinch Ops local DB); HubSpot is only a
          fallback pre-fill when no AE submission has been saved yet. */}
      <section className="space-y-3 rounded-md border bg-muted/30 p-3">
        <div className="flex items-baseline justify-between">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Forecast Submission · Wk {isoWeek}
          </Label>
          <Link
            to="/cro-forecast/ae/$repId"
            params={{ repId: rep.rep_id }}
            search={{ year: isoYear, week: isoWeek }}
            className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-power"
          >
            View AE detail <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Saved per ISO week into{" "}
          <code className="rounded bg-muted px-1 font-mono">weekly_ae_calls</code>{" "}
          — Cinch Ops keeps the historical record. The "HubSpot" baseline is
          only used as the pre-fill when no AE submission exists yet for the
          month. Each save creates / updates the row for the current week.
        </p>
        <div className="space-y-3">
          {rep.months.map((m) => {
            // Past months relative to the viewed iso-week are read-only —
            // they've already closed, so the forecast is settled. We
            // render the actual closed amount instead of the input so
            // Stan can see how that month landed without being able to
            // rewrite the historical call.
            const isPastMonth = m.month < currentFiscalMonth;
            if (isPastMonth) {
              return (
                <div
                  key={m.month}
                  className="flex items-center gap-3 rounded border bg-muted/30 px-3 py-2"
                >
                  <span className="w-16 text-xs font-medium text-muted-foreground">
                    {fmtMonthName(m.month)}
                  </span>
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">
                    Closed
                  </span>
                  <span className="font-mono text-sm font-semibold text-success">
                    {fmtCents(m.closed_cents ?? 0)}
                  </span>
                  <span className="ml-auto inline-flex items-center gap-1 rounded-full border border-muted-foreground/30 bg-card px-2 py-0.5 text-[10px] text-muted-foreground">
                    Locked · month ended
                  </span>
                </div>
              );
            }

            const fromHubspot = m.hubspot_submission_cents ?? 0;
            const fromLocked = m.submitted_cents;
            const baseline = fromLocked ?? fromHubspot;
            const baselineSource =
              fromLocked != null
                ? `Locked · ${m.submitted_at_iso_week ?? ""}`
                : fromHubspot > 0
                  ? "HubSpot"
                  : "—";
            const current = draftSubmission[m.month] ?? 0;
            const reasoning = draftReasoning[m.month] ?? "";
            const isDirty =
              current !== baseline ||
              reasoning !== (m.submitted_reasoning ?? "");
            return (
              <div
                key={m.month}
                className="space-y-1.5 rounded border bg-card px-3 py-2"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="w-16 text-xs font-medium">
                    {fmtMonthName(m.month)}
                  </span>
                  <Input
                    type="number"
                    value={Math.round(current / 100) || 0}
                    onChange={(e) =>
                      setDraftSubmission({
                        ...draftSubmission,
                        [m.month]: Math.round((Number(e.target.value) || 0) * 100),
                      })
                    }
                    className="w-32 font-mono"
                    placeholder="0"
                  />
                  <span className="font-mono text-xs text-muted-foreground">
                    ≈ {fmtCents(current)}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] ${
                      fromLocked != null
                        ? "border-success/40 bg-success/10 text-success"
                        : fromHubspot > 0
                          ? "border-power/40 bg-power/10 text-power"
                          : "border-border bg-muted text-muted-foreground"
                    }`}
                    title={`Baseline source: ${baselineSource}`}
                  >
                    {baselineSource}
                  </span>
                  {isDirty && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-[11px]"
                      onClick={() => resetSubmissionFor(m.month)}
                      title="Reset to baseline"
                    >
                      Reset
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => saveSubmissionFor(m.month)}
                    variant={isDirty ? "default" : "outline"}
                  >
                    Save
                  </Button>
                </div>
                <Textarea
                  value={reasoning}
                  onChange={(e) =>
                    setDraftReasoning({
                      ...draftReasoning,
                      [m.month]: e.target.value,
                    })
                  }
                  rows={2}
                  placeholder={`Why this number? Risks, push-outs, big shifts… (saved with the Wk ${isoWeek} submission)`}
                  className="resize-y text-xs"
                />
                {(m.submission_history?.length ?? 0) > 0 && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-[11px] text-muted-foreground hover:text-foreground">
                      {m.submission_history!.length} prior week
                      {m.submission_history!.length === 1 ? "" : "s"} of locked submissions
                    </summary>
                    <table className="mt-2 w-full text-[11px]">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                          <th className="py-1 text-left">Week</th>
                          <th className="py-1 text-right">Submitted</th>
                          <th className="py-1 text-left">Source</th>
                          <th className="py-1 text-left">Reasoning</th>
                        </tr>
                      </thead>
                      <tbody>
                        {m.submission_history!.map((h, i) => (
                          <tr key={i} className="border-t border-border/60">
                            <td className="py-1 font-mono">
                              {h.iso_year}-W{String(h.iso_week).padStart(2, "0")}
                            </td>
                            <td className="py-1 text-right font-mono">
                              {fmtCents(h.call_cents)}
                            </td>
                            <td className="py-1 text-muted-foreground">
                              {h.source}
                            </td>
                            <td className="py-1 text-muted-foreground">
                              {h.reasoning || "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </details>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Stan's weekly override — applied ACROSS the quarter's still-open
          months and sitting directly ABOVE the CRO call block. Enter a % and
          hit Apply to set every month's call to that % of the AE's HubSpot
          Forecast Submission for that month; each month stays hand-editable
          before saving. */}
      <section className="space-y-2 rounded-md border bg-muted/30 p-3">
        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Stan's override % — {rep.display_name}, Wk {isoWeek}
        </Label>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="number"
            min={0}
            max={500}
            value={overridePct}
            onChange={(e) => setOverridePct(Number(e.target.value) || 0)}
            className="w-24 font-mono"
          />
          <span className="text-xs text-muted-foreground">
            % of each month's HubSpot submission
          </span>
          <Button
            size="sm"
            onClick={applyOverride}
            disabled={overrideMutation.isPending}
          >
            Apply to all months
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Sets each still-open month's CRO call to{" "}
          <span className="font-mono">{fmtPct(overridePct, 0)}</span> of the
          AE's HubSpot Forecast Submission for that month. Fine-tune any month
          below, then <span className="font-medium">Save all</span> to push the
          call through to the Summary.
        </p>
      </section>

      <div className={`grid grid-cols-1 gap-4 ${expanded ? "md:grid-cols-2" : ""}`}>
        {/* Per-month CRO call edit */}
        <section className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
              CRO call — month-by-month
            </Label>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-[11px]"
              onClick={saveAllCalls}
              disabled={callMutation.isPending || !anyCallDirty}
            >
              Save all
            </Button>
          </div>
          {rep.months.map((m) => {
            // Same past-month guard as Forecast Submission above — once
            // the month has ended, the CRO call is settled. Show what
            // closed instead of letting Stan rewrite history.
            const isPastMonth = m.month < currentFiscalMonth;
            if (isPastMonth) {
              return (
                <div key={m.month} className="flex items-center gap-2 text-xs">
                  <span className="w-16 text-muted-foreground">
                    {fmtMonthName(m.month)}
                  </span>
                  <span className="uppercase tracking-wider text-muted-foreground">
                    Closed
                  </span>
                  <span className="font-mono font-semibold text-success">
                    {fmtCents(m.closed_cents ?? 0)}
                  </span>
                </div>
              );
            }
            const dirty = (draftCall[m.month] ?? 0) !== m.call_cents;
            const hubspotBase = m.hubspot_submission_cents ?? 0;
            return (
              <div key={m.month} className="flex items-center gap-2">
                <span className="w-16 text-xs text-muted-foreground">
                  {fmtMonthName(m.month)}
                </span>
                <Input
                  type="number"
                  value={Math.round((draftCall[m.month] ?? 0) / 100) || 0}
                  onChange={(e) =>
                    setDraftCall({
                      ...draftCall,
                      [m.month]: Math.round((Number(e.target.value) || 0) * 100),
                    })
                  }
                  className="w-32 font-mono"
                />
                <span className="text-xs text-muted-foreground">
                  ({fmtCents(draftCall[m.month] ?? 0)})
                </span>
                {hubspotBase > 0 && (
                  <span
                    className="text-[10px] text-muted-foreground"
                    title="AE's HubSpot Forecast Submission for this month — the base the override % applies to"
                  >
                    HS {fmtCents(hubspotBase)}
                  </span>
                )}
                <Button
                  size="sm"
                  variant={dirty ? "default" : "outline"}
                  onClick={() => saveCall(m.month)}
                  disabled={!dirty || callMutation.isPending}
                >
                  Save
                </Button>
              </div>
            );
          })}
        </section>

        {/* Notes */}
        <section className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Stan's notes — for the meeting
            </Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              placeholder={`Talking points for ${rep.display_name}…`}
            />
            <Button
              size="sm"
              variant="outline"
              onClick={saveNote}
              disabled={noteMutation.isPending || note === (rep.notes ?? "")}
            >
              Save notes
            </Button>
          </div>
        </section>
      </div>

      {/* Deals INCLUDED in the AE's HubSpot Forecast Submission for the
          current month — what they're actually calling, not just every
          open deal they own. Surfaces above the broader open-deals list
          since this is the answer to "what's in the number?" at the top
          of the meeting. */}
      {rep.forecast_deals.length > 0 && (
        <section className="space-y-1.5">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {rep.forecast_deals.length} deals in this forecast
          </Label>
          <p className="text-[11px] text-muted-foreground">
            Deals the AE explicitly included in their HubSpot Forecast Submission
            for this month — the source of the {fmtCentsWhole(rep.month_submission_cents)} call.
          </p>
          <div className={`grid gap-1.5 ${expanded ? "md:grid-cols-2" : "grid-cols-1"}`}>
            {rep.forecast_deals.slice(0, expanded ? 24 : 6).map((d) => (
              <a
                key={d.hubspot_deal_id}
                href={hubspotDealUrl(d.hubspot_deal_id)}
                target="_blank"
                rel="noreferrer noopener"
                className="flex items-center justify-between gap-2 rounded border bg-card px-2 py-1 text-xs hover:bg-muted"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="truncate">{d.name}</span>
                  <DealMeta
                    stageName={d.stage_name}
                    closeDate={d.close_date}
                    createdDate={d.created_date}
                  />
                </span>
                <span className="font-mono text-muted-foreground">
                  {fmtCents(d.amount_cents)}
                </span>
              </a>
            ))}
          </div>
          {rep.forecast_deals.length > (expanded ? 24 : 6) && (
            <p className="text-[11px] text-muted-foreground">
              + {rep.forecast_deals.length - (expanded ? 24 : 6)} more
            </p>
          )}
        </section>
      )}

      {rep.open_deals.length > 0 && (
        <section className="space-y-1.5">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {rep.open_deals.length} open deals — all owned by the AE
          </Label>
          <p className="text-[11px] text-muted-foreground">
            Current stage and HubSpot close date on each — a deal whose close
            date has already passed is flagged, as is one sitting before Sales
            Qualified and therefore outside the pipeline columns.
          </p>
          <div className={`grid gap-1.5 ${expanded ? "md:grid-cols-2" : "grid-cols-1"}`}>
            {rep.open_deals.slice(0, expanded ? 24 : 6).map((d) => (
              <a
                key={d.hubspot_deal_id}
                href={hubspotDealUrl(d.hubspot_deal_id)}
                target="_blank"
                rel="noreferrer noopener"
                className="flex items-center justify-between gap-2 rounded border bg-card px-2 py-1 text-xs hover:bg-muted"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="truncate">{d.name}</span>
                  <DealMeta
                    stageName={d.stage_name}
                    closeDate={d.close_date}
                    createdDate={d.created_date}
                    countsTowardPipeline={d.counts_toward_pipeline}
                  />
                </span>
                <span className="font-mono text-muted-foreground">
                  {fmtCents(d.amount_cents)}
                </span>
              </a>
            ))}
          </div>
          {rep.open_deals.length > (expanded ? 24 : 6) && (
            <p className="text-[11px] text-muted-foreground">
              + {rep.open_deals.length - (expanded ? 24 : 6)} more on the AE detail page
            </p>
          )}
        </section>
      )}
    </div>
  );
}

/**
 * Sub-line under a deal name: current pipeline stage + forecasted close date.
 *
 * A close date already in the past is the single most useful thing to spot in
 * this list — it means the deal is in the AE's number but its own date says it
 * should already have landed — so it renders in the danger tone rather than as
 * muted text. Dates are compared date-only against local today; HubSpot stores
 * closedate as a plain YYYY-MM-DD so there's no timezone to reconcile.
 */
function DealMeta({
  stageName,
  closeDate,
  createdDate,
  countsTowardPipeline,
}: {
  stageName?: string | null;
  closeDate?: string | null;
  /** ISO date the deal was opened — paired with the close date it shows age. */
  createdDate?: string | null;
  /** When explicitly false, the deal sits outside the pipeline columns above. */
  countsTowardPipeline?: boolean;
}) {
  if (!stageName && !closeDate && !createdDate) return null;
  const overdue = closeDate ? isPastDate(closeDate) : false;
  const excluded = countsTowardPipeline === false;
  const ageDays = createdDate ? daysSince(createdDate) : null;
  return (
    <span className="flex flex-wrap items-center gap-x-1.5 text-[10px] text-muted-foreground">
      {stageName && <span>{stageName}</span>}
      {stageName && createdDate && <span aria-hidden>·</span>}
      {/* Created date carries its age inline — "opened Mar 3, 2026" alone makes
          the reader do the subtraction, and deal age is the reason to show it. */}
      {createdDate && (
        <span>
          opened {fmtShortDate(createdDate)}
          {ageDays != null && ` (${ageDays}d)`}
        </span>
      )}
      {createdDate && closeDate && <span aria-hidden>·</span>}
      {!createdDate && stageName && closeDate && <span aria-hidden>·</span>}
      {closeDate && (
        <span className={overdue ? "font-semibold text-danger" : undefined}>
          {overdue ? "overdue " : "closes "}
          {fmtShortDate(closeDate)}
        </span>
      )}
      {/* Pre-SQO deals are listed but aren't in Total/Quarter Pipeline. Without
          this the drawer's rows can't be added up to the columns above it. */}
      {excluded && (
        <>
          <span aria-hidden>·</span>
          <span className="italic">not in pipeline total</span>
        </>
      )}
    </span>
  );
}

/** `YYYY-MM-DD` → e.g. "Sep 17, 2026". Falls back to the raw string. */
function fmtShortDate(iso: string): string {
  const d = parseIsoDate(iso);
  if (d == null) return iso;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Whole days between a date-only ISO string and today. Null if unparseable. */
function daysSince(iso: string): number | null {
  const d = parseIsoDate(iso);
  if (d == null) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((today.getTime() - d.getTime()) / 86_400_000);
  return days >= 0 ? days : null;
}

function isPastDate(iso: string): boolean {
  const d = parseIsoDate(iso);
  if (d == null) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d.getTime() < today.getTime();
}

/** Parse a date-only ISO string in LOCAL time — `new Date("2026-09-17")` would
 *  be parsed as UTC midnight and can render as the previous day west of UTC. */
function parseIsoDate(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

// ── helpers (kept private to the drawer) ─────────────────────────────

function usePerAeNote(
  isoYear: number,
  isoWeek: number,
  currentBlob: Record<string, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { rep_id: string; body: string }) => {
      const next = { ...currentBlob, [payload.rep_id]: payload.body };
      return apiFetch(`/cro_forecast/notes`, {
        method: "PUT",
        body: {
          iso_year: isoYear,
          iso_week: isoWeek,
          scope: "per_ae_notes",
          body: JSON.stringify(next),
        },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["cro_forecast", "forecast-meeting"] });
    },
  });
}

function useUpsertOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      iso_year: number;
      iso_week: number;
      rep_id: string;
      override_pct: number;
      note?: string | null;
    }) =>
      apiFetch(`/cro_forecast/weekly-override`, {
        method: "PUT",
        body: payload,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["cro_forecast", "forecast-meeting"] });
    },
  });
}
